"use client"

/**
 * Interactivity Hooks for Outfame Components
 *
 * Consolidated hooks for interactive functionality.
 */
import { useState, useRef, useEffect, useCallback } from "react"
import { usePathname } from "next/navigation"

// ============================================
// DROPDOWN HOOK
// Used by: Navigation.tsx
// ============================================
export function useDropdown() {
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const overlayRef = useRef<HTMLDivElement>(null)
    const isHoveringPanelRef = useRef(false)

    const cancelClose = useCallback(() => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current)
            closeTimeoutRef.current = null
        }
    }, [])

    const closeImmediate = useCallback(() => {
        setActiveDropdown(null)

        if (overlayRef.current) {
            overlayRef.current.style.backdropFilter = "blur(0px)"
            ;(overlayRef.current.style as any).webkitBackdropFilter = "blur(0px)"
            overlayRef.current.style.backgroundColor = "rgba(0, 0, 0, 0)"
        }
    }, [])

    const open = useCallback(
        (dropdownId: string) => {
            cancelClose()
            setActiveDropdown(dropdownId)

            if (overlayRef.current) {
                overlayRef.current.style.backdropFilter = "blur(5px)"
                ;(overlayRef.current.style as any).webkitBackdropFilter = "blur(5px)"
                overlayRef.current.style.backgroundColor = "rgba(0, 0, 0, 0.1)"
            }
        },
        [cancelClose]
    )

    const close = useCallback(() => {
        closeImmediate()
    }, [closeImmediate])

    const scheduleClose = useCallback(
        (delay = 150) => {
            cancelClose()
            closeTimeoutRef.current = setTimeout(() => {
                if (!isHoveringPanelRef.current) {
                    closeImmediate()
                }
            }, delay)
        },
        [cancelClose, closeImmediate]
    )

    const handleTriggerEnter = useCallback(
        (dropdownId: string) => {
            cancelClose()
            open(dropdownId)
        },
        [cancelClose, open]
    )

    const handleTriggerLeave = useCallback(() => {
        scheduleClose(150)
    }, [scheduleClose])

    const handleTriggerClick = useCallback(
        (dropdownId: string) => {
            if (activeDropdown === dropdownId) {
                isHoveringPanelRef.current = false
                closeImmediate()
            } else {
                open(dropdownId)
            }
        },
        [activeDropdown, closeImmediate, open]
    )

    const handlePanelEnter = useCallback(() => {
        cancelClose()
        isHoveringPanelRef.current = true
    }, [cancelClose])

    const handlePanelLeave = useCallback(() => {
        isHoveringPanelRef.current = false
        scheduleClose(100)
    }, [scheduleClose])

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") close()
        }
        document.addEventListener("keydown", handleKeyDown)
        return () => document.removeEventListener("keydown", handleKeyDown)
    }, [close])

    // Close dropdown on route change
    const pathname = usePathname()
    useEffect(() => {
        closeImmediate()
    }, [pathname, closeImmediate])

    return {
        activeDropdown,
        overlayRef,
        open,
        close,
        handleTriggerEnter,
        handleTriggerLeave,
        handleTriggerClick,
        handlePanelEnter,
        handlePanelLeave,
        isOpen: (id: string) => activeDropdown === id
    }
}

// ============================================
// MOBILE MENU HOOK
// Used by: Navigation.tsx
// Premium full-screen menu with scroll shadows
// ============================================
export function useMobileMenu() {
    const [isOpen, setIsOpen] = useState(false)
    const [expandedAccordion, setExpandedAccordion] = useState<string | null>(null)
    const [canScrollTop, setCanScrollTop] = useState(false)
    const [canScrollBottom, setCanScrollBottom] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const scrollRef = useRef<HTMLDivElement>(null)
    const scrollPositionRef = useRef(0)
    // Sync ref for close guards — avoids restoring scroll when menu is already closed
    const isOpenRef = useRef(false)

    // Check scroll position for shadows
    const checkScroll = useCallback(() => {
        const el = scrollRef.current
        if (!el) return
        const { scrollTop, scrollHeight, clientHeight } = el
        setCanScrollTop(scrollTop > 5)
        setCanScrollBottom(scrollTop + clientHeight < scrollHeight - 5)
    }, [])

    const toggle = useCallback(() => {
        setIsOpen((prev) => {
            if (prev) {
                // Closing - restore scroll
                isOpenRef.current = false
                document.body.style.overflow = ""
                document.body.style.position = ""
                document.body.style.top = ""
                document.body.style.width = ""
                window.scrollTo(0, scrollPositionRef.current)
                setExpandedAccordion(null)
            } else {
                // Opening - lock scroll
                isOpenRef.current = true
                scrollPositionRef.current = window.scrollY
                document.body.style.overflow = "hidden"
                document.body.style.position = "fixed"
                document.body.style.top = `-${scrollPositionRef.current}px`
                document.body.style.width = "100%"
            }
            return !prev
        })
    }, [])

    const open = useCallback(() => {
        // Save scroll position before locking
        scrollPositionRef.current = window.scrollY
        isOpenRef.current = true
        setIsOpen(true)
        // Lock body scroll - iOS-safe approach
        document.body.style.overflow = "hidden"
        document.body.style.position = "fixed"
        document.body.style.top = `-${scrollPositionRef.current}px`
        document.body.style.width = "100%"
    }, [])

    const close = useCallback(() => {
        if (!isOpenRef.current) return // Already closed — no-op
        isOpenRef.current = false
        setIsOpen(false)
        setExpandedAccordion(null)
        // Restore scroll position
        document.body.style.overflow = ""
        document.body.style.position = ""
        document.body.style.top = ""
        document.body.style.width = ""
        window.scrollTo(0, scrollPositionRef.current)
    }, [])

    // Close for navigation — unlocks body without scroll restoration.
    // RouterEventWrapper handles scroll-to-top on route change.
    const closeForNavigation = useCallback(() => {
        if (!isOpenRef.current) return
        isOpenRef.current = false
        setIsOpen(false)
        setExpandedAccordion(null)
        document.body.style.overflow = ""
        document.body.style.position = ""
        document.body.style.top = ""
        document.body.style.width = ""
        // No window.scrollTo — let Next.js + RouterEventWrapper handle it
    }, [])

    const toggleAccordion = useCallback((id: string) => {
        setExpandedAccordion((prev) => (prev === id ? null : id))
        // Re-check scroll after accordion animation completes
        setTimeout(checkScroll, 220)
    }, [checkScroll])

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 992 && isOpen) {
                close()
            }
        }
        window.addEventListener("resize", handleResize)
        return () => window.removeEventListener("resize", handleResize)
    }, [isOpen, close])

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isOpen) close()
        }
        document.addEventListener("keydown", handleKeyDown)
        return () => document.removeEventListener("keydown", handleKeyDown)
    }, [isOpen, close])

    // Close menu on route change (e.g., back/forward navigation)
    const pathname = usePathname()
    useEffect(() => {
        if (isOpenRef.current) {
            closeForNavigation()
        }
    }, [pathname, closeForNavigation])

    // Initial scroll check when menu opens
    useEffect(() => {
        if (isOpen) {
            setTimeout(checkScroll, 50)
        }
    }, [isOpen, checkScroll])

    return {
        isOpen,
        menuRef,
        scrollRef,
        expandedAccordion,
        canScrollTop,
        canScrollBottom,
        checkScroll,
        toggle,
        open,
        close,
        closeForNavigation,
        toggleAccordion,
        isAccordionOpen: (id: string) => expandedAccordion === id
    }
}

// ============================================
// FAQ ACCORDION HOOK
// Used by: FAQ.tsx
// ============================================
export function useFAQAccordion() {
    const [openItem, setOpenItem] = useState<number | null>(null)

    const toggle = useCallback((index: number) => {
        setOpenItem((prev) => (prev === index ? null : index))
    }, [])

    const open = useCallback((index: number) => {
        setOpenItem(index)
    }, [])

    const close = useCallback(() => {
        setOpenItem(null)
    }, [])

    return {
        openItem,
        toggle,
        open,
        close,
        isOpen: (index: number) => openItem === index
    }
}

// ============================================
// CAROUSEL HOOK
// Used by: Testimonials, FeatureCarousel
// ============================================
export function useCarousel(
    totalSlides: number,
    options?: {
        autoPlay?: boolean
        autoPlayInterval?: number
        initialIndex?: number
    }
) {
    const { autoPlay = false, autoPlayInterval = 5000, initialIndex = 0 } = options || {}

    const [currentIndex, setCurrentIndex] = useState(initialIndex)
    const trackRef = useRef<HTMLDivElement>(null)
    const autoPlayRef = useRef<NodeJS.Timeout | null>(null)
    const [isPaused, setIsPaused] = useState(false)

    const maxIndex = totalSlides - 1

    const goTo = useCallback(
        (index: number) => {
            const newIndex = Math.max(0, Math.min(index, maxIndex))
            setCurrentIndex(newIndex)
        },
        [maxIndex]
    )

    const next = useCallback(() => {
        setCurrentIndex((prev) => (prev < maxIndex ? prev + 1 : 0))
    }, [maxIndex])

    const prev = useCallback(() => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : maxIndex))
    }, [maxIndex])

    useEffect(() => {
        if (autoPlay && !isPaused) {
            autoPlayRef.current = setInterval(next, autoPlayInterval)
        }
        return () => {
            if (autoPlayRef.current) clearInterval(autoPlayRef.current)
        }
    }, [autoPlay, autoPlayInterval, isPaused, next])

    const pause = useCallback(() => setIsPaused(true), [])
    const resume = useCallback(() => setIsPaused(false), [])

    return {
        currentIndex,
        trackRef,
        goTo,
        next,
        prev,
        pause,
        resume,
        isActive: (index: number) => currentIndex === index
    }
}

// ============================================
// COOKIE CONSENT HOOK
// Used by: CookieBanner.tsx
// ============================================
const STORAGE_KEY = "outfame_cookie_consent"
const CONSENT_DURATION = 365 // days

export function useCookieConsent() {
    const [showBanner, setShowBanner] = useState(false)
    const [consent, setConsent] = useState<boolean | null>(null)

    useEffect(() => {
        // Check for existing consent
        const stored = getStoredConsent()
        if (stored === null) {
            // Show banner after delay
            const timer = setTimeout(() => setShowBanner(true), 2000)
            return () => clearTimeout(timer)
        }
        setConsent(stored)
    }, [])

    const getStoredConsent = (): boolean | null => {
        if (typeof window === "undefined") return null
        try {
            const stored = localStorage.getItem(STORAGE_KEY)
            if (!stored) return null

            const { consent, timestamp } = JSON.parse(stored)
            const now = Date.now()
            const maxAge = CONSENT_DURATION * 24 * 60 * 60 * 1000

            if (now - timestamp > maxAge) {
                localStorage.removeItem(STORAGE_KEY)
                return null
            }
            return consent
        } catch {
            return null
        }
    }

    const saveConsent = (value: boolean) => {
        try {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                    consent: value,
                    timestamp: Date.now()
                })
            )
        } catch {
            console.warn("Unable to save cookie consent")
        }
    }

    const accept = useCallback(() => {
        saveConsent(true)
        setConsent(true)
        setShowBanner(false)
    }, [])

    const decline = useCallback(() => {
        saveConsent(false)
        setConsent(false)
        setShowBanner(false)
    }, [])

    return {
        showBanner,
        consent,
        accept,
        decline
    }
}
